import { SearchBrandScheme, SupplyTypeEnum } from '@/apis/rest/generated/types'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import { TrackerOrderByEnum } from '@/constants/tracker-sort-by-value.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { AdPetType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { ReturnConvertProductSchemeType } from '@/utils/product/product-card.util'
import { ReferrerUtil } from '@/utils/referrer.util'
import { decodeIdForEventTracker } from '@/utils/utilApi'

type TrackViewSearchResultParams = {
  searchLocation?: string | string[]
  filterPriceMax: number | null
  filterCategory: string[] | null
  searchKeyword: string | string[]
  isDirectResult: boolean
  searchOutofstockCount?: number
  filterPriceMin: number | null
  petTypeSearch: AdPetType | AdPetType.All | AdPetType.Cat | AdPetType.Dog
  filterBrand: string[] | null
  searchResultCount?: number
  sortBy: TrackerOrderByEnum
  excludeOutofstock: boolean
  search_engine?: string
  search_query?: string
}

type TrackClickSearchListParams = {
  productId: number | string
  searchPetType: AdPetType | AdPetType.All | AdPetType.Cat | AdPetType.Dog
  isPetTypeSearchCat: boolean
  productDiscountRate: number
  searchKeyword: string | string[]
  productName: string
  productIsInStock: boolean
  searchListIndex: number
  productReviewAvgScore: number
  isPetTypeSearchDog: boolean
  productPurchasePrice: number
  searchResultCount?: number
  productPrice: number
  productReviewTotalCount: number
}

export type SearchPageRecentlyProductCardData = ReturnConvertProductSchemeType & {
  boughtCount: number
}

export const useSearchEventTracker = () => {
  const tracker = useTracker()
  const referrer = ReferrerUtil.getReferrer()

  const trackViewSearchResultEvent = (property: TrackViewSearchResultParams) => {
    const referrerDomain = ReferrerUtil.getReferrerDomain(referrer)
    const searchQuery = ReferrerUtil.getSearchQuery(referrer)

    const trackerProperties = {
      ...property,
    }

    if (referrerDomain) {
      trackerProperties['search_engine'] = referrerDomain
    }
    if (searchQuery) {
      trackerProperties['search_query'] = searchQuery
    }

    tracker.triggerCustomEvent(FitpetMallEventEnum.ViewSearchResult, trackerProperties)
  }

  const trackClickSearchListProductEvent = (property: TrackClickSearchListParams) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickSearchListProduct, property)
  }

  /**
   * 검색결과 화면 최근 구매한 상품 위젯의 상품 클릭 시
   */
  const trackClickSearchResultFrequentProduct = ({
    searchKeyword,
    searchLocation,
    petType,
    productId,
    productName,
    boughtCount,
  }: {
    searchKeyword: string
    searchLocation: string
    petType: AdPetType
    productId: number
    productName: string
    boughtCount: number
  }) => {
    const properties = {
      search_keyword: searchKeyword,
      search_location: searchLocation,
      purchase_count: boughtCount,
      product_id: productId,
      product_name: productName,
      pet_type_search: petType,
    }
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickSearchResultFrequentProduct, properties)
  }

  /**
   * 검색결과 화면 최근 구매한 상품 위젯의 상품 조회(노출) 시
   */
  const trackViewSearchResultFrequentProduct = ({
    product,
    boughtCount,
    searchKeyword,
    searchLocation,
    petType,
    totalCount,
    soldOutCount,
  }: {
    product: ReturnConvertProductSchemeType
    boughtCount: number
    searchKeyword: string
    searchLocation: string
    petType: AdPetType
    totalCount?: number
    soldOutCount?: number
  }) => {
    const properties = {
      search_keyword: searchKeyword,
      search_location: searchLocation,
      purchase_count: boughtCount,
      product_id: decodeIdForEventTracker(product.id),
      product_name: product.name,
      pet_type_search: petType,
      search_result_count: totalCount,
      search_outofstock_count: soldOutCount,
    }
    tracker.triggerCustomEvent(FitpetMallEventEnum.ViewSearchResultFrequentProduct, properties)
  }

  /**
   * 검색결과 페이지에서 상품 클릭 후, 페이지 뒤로가기로 이탈 시 노출되는 방금본 상품과 관련된 상품리스트 view트래커
   */
  const trackViewSearchResultSimilarProductSection = ({
    addLocation,
    sectionName,
    searchKeyword,
    searchLocation,
    originSupplyType,
    originBrandId,
    originBrandName,
    originProductId,
    originProductName,
    petTypeSearch,
  }: {
    addLocation: string
    sectionName: string
    searchKeyword: string
    searchLocation: string
    originSupplyType: SupplyTypeEnum
    originBrandId?: number
    originBrandName: string
    originProductId: number
    originProductName: string
    petTypeSearch: AdPetType
  }) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ViewSearchResultSimilarProductSection, {
      list_type: 'similarproduct',
      location: 'search',
      add_location: addLocation,
      section_name: sectionName,
      search_keyword: searchKeyword,
      search_location: searchLocation,
      origin_supply_type: originSupplyType,
      origin_brand_id: originBrandId,
      origin_brand_name: originBrandName,
      origin_product_id: originProductId,
      origin_product_name: originProductName,
      pet_type_search: petTypeSearch,
    })
  }

  /**
   * 검색결과 화면 브랜드 영역 상품 위젯의 상품 조회(노출) 시
   */
  const trackViewSearchResultBrandProduct = ({
    searchKeyword,
    petType,
    searchLocation,
    brand,
  }: // product,
  {
    searchKeyword: string
    petType: AdPetType
    searchLocation: string
    brand: SearchBrandScheme
  }) => {
    const properties = {
      search_keyword: searchKeyword,
      pet_type_search: petType,
      search_location: searchLocation,
      brand_id: brand.id,
      brand_name: brand.name,
      type: 'Brand',
    }
    tracker.triggerCustomEvent(FitpetMallEventEnum.ViewSearchResultBrandProduct, properties)
  }

  /**
   * 검색결과 화면 브랜드 영역 상품 위젯의 상품 클릭 시
   */
  const trackClickSearchResultBrandProduct = ({
    searchKeyword,
    petType,
    searchLocation,
    brand,
    product,
  }: {
    searchKeyword: string
    petType: AdPetType
    searchLocation: string
    brand: SearchBrandScheme
    product: ProductTypeDefinedByFrontend
  }) => {
    const properties = {
      search_keyword: searchKeyword,
      pet_type_search: petType,
      search_location: searchLocation,
      product_id: product.id,
      product_name: product.name,
      brand_id: brand.id,
      brand_name: brand.name,
      type: 'Brand',
    }
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickSearchResultBrandProduct, properties)
  }

  return {
    trackViewSearchResultEvent,
    trackClickSearchListProductEvent,
    trackViewSearchResultFrequentProduct,
    trackClickSearchResultFrequentProduct,
    trackViewSearchResultSimilarProductSection,
    trackViewSearchResultBrandProduct,
    trackClickSearchResultBrandProduct,
  }
}
